import { formatMoney } from '@/utils/format';
import { channelLabel, type BulkTotals } from '@/utils/bulkPayments';
import type { BulkChannel } from '@/types/portal';

interface SourceWalletCardProps {
  walletName: string;
  currency: string;
  balance: number;
  accountNumber: string;
}

export function SourceWalletCard({
  walletName,
  currency,
  balance,
  accountNumber,
}: SourceWalletCardProps) {
  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center gap-2 border-b border-background-200 px-5 py-4">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-200 text-base text-foreground-600">
          <i className="ri-wallet-3-line" />
        </span>
        <h3 className="font-heading text-sm font-semibold text-foreground-950">Source Wallet</h3>
      </header>
      <dl className="space-y-3 px-5 py-4">
        <div>
          <dt className="text-xs text-foreground-500">Wallet Name</dt>
          <dd className="mt-0.5 text-sm font-medium text-foreground-950">
            {walletName || '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-foreground-500">Current Balance</dt>
          <dd className="mt-0.5 text-sm font-medium text-foreground-950">
            {walletName ? formatMoney(balance, currency) : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-foreground-500">Account Number</dt>
          <dd className="mt-0.5 text-sm font-medium text-foreground-950">{accountNumber}</dd>
        </div>
      </dl>
    </section>
  );
}

interface TransactionSummaryPanelProps {
  totals: BulkTotals;
  currency: string;
  channel: BulkChannel;
}

export function TransactionSummaryPanel({
  totals,
  currency,
  channel,
}: TransactionSummaryPanelProps) {
  const rows = [
    { label: 'Total transactions', value: String(totals.validCount + totals.blockedCount), tone: '' },
    { label: 'Valid transactions', value: String(totals.validCount), tone: 'text-accent-700' },
    { label: 'Blocked', value: String(totals.blockedCount), tone: totals.blockedCount ? 'text-primary-700' : '' },
  ];

  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center gap-2 border-b border-background-200 px-5 py-4">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-base text-accent-700">
          <i className="ri-file-list-3-line" />
        </span>
        <h3 className="font-heading text-sm font-semibold text-foreground-950">Transaction Summary</h3>
      </header>
      <div className="px-5 py-4">
        <dl className="space-y-3">
          {rows.map((row) => (
            <div key={row.label} className="flex items-center justify-between">
              <dt className="text-xs text-foreground-500">{row.label}</dt>
              <dd className={`text-sm font-semibold ${row.tone || 'text-foreground-950'}`}>
                {row.value}
              </dd>
            </div>
          ))}
        </dl>

        <div className="mt-4 border-t border-background-100 pt-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-foreground-500">Total amount</span>
            <span className="font-heading text-lg font-semibold text-foreground-950">
              {formatMoney(totals.total, currency)}
            </span>
          </div>
          {totals.sweep > 0 ? (
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-foreground-500">Auto-swept to pool</span>
              <span className="font-medium text-secondary-900">
                {formatMoney(totals.sweep, currency)}
              </span>
            </div>
          ) : null}
          <div className="mt-2 flex items-center justify-between text-xs">
            <span className="text-foreground-500">To creators</span>
            <span className="font-medium text-accent-700">
              {formatMoney(totals.toCreators, currency)}
            </span>
          </div>
        </div>

        <p className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-background-100 px-2.5 py-1.5 text-[11px] text-foreground-600">
          <i className="ri-route-line" />
          {channelLabel(channel)}
        </p>
      </div>
    </section>
  );
}